import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  BRAND_DEFAULTS,
  BRAND_FONTS,
  BRAND_LIMITS,
  type BrandFont,
  type BrandRadius,
  type BrandStyleSettings,
} from '@up2d8/contracts';
import tokens from '@up2d8/ui/tokens.json';
import { checkColour, colourState, effectiveHex, type ColourState } from '../../shared/colour';
import { brandFontStack, ensureBrandFont } from '../../shared/brand-fonts';
import { formatNumber } from '../../shared/format';
import { RADII } from '../../shared/theme';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Checkbox,
  Field,
  Input,
  Modal,
  Note,
  Segmented,
  Select,
  Textarea,
} from '../admin-kit';
import { ColourPicker } from './colour-picker';

/**
 * De kaarten die Huisstijl (`/admin/huisstijl`) en Merk (`/master/merk`) delen (MRK-3).
 *
 * Twee niveaus, één formulier: een veld leeg laten is erven (ADR-0084). Elk veld zegt daarom twee
 * dingen: wat er nu gekozen is (`own`, een badge "Eigen keuze"), en wat er geldt als het leeg is
 * (`inherited`, de hint "Leeg laten = …"). Op het platform is dat tweede de standaard van UP2D8; bij
 * een bedrijf het opgeloste platformmerk.
 *
 * Wie welk niveau is, weet deze module niet - alleen of het hint "van het platformmerk" of "de
 * standaard" zegt. Opslaan, uploaden en de rechten zitten in de schermen.
 */

/**
 * De kleuren van UP2D8 als niemand iets koos. Uit `tokens.json` - de bron van de tokens, en de
 * enige plek waar die hexcodes mogen staan - en niet als letterlijke waarde in een scherm.
 */
export const DEFAULT_COLOURS = {
  primary: tokens.color['brand-primary'],
  accent: tokens.color['brand-orange'],
  dark: tokens.color['brand-dark-blue'],
} as const;

/**
 * Het donkere basisvlak waarmee de CSS de zijbalk afleidt bij een eigen huisstijl (tokens.css,
 * `--brand-dark-blue` onder `[data-brand='custom']`). Geen merkkleur: bijna zwart, om mee te mengen.
 */
const DARK_MIX_BASE = '#0e1230';

export type BrandLevel = 'tenant' | 'platform';

/** Wat er in het formulier staat, vóór het opslaan. Leeg (`''`) = erven. */
export interface BrandDraft {
  primary: ColourState;
  accent: ColourState;
  sidebar: ColourState;
  fontHeading: BrandFont | '';
  fontBody: BrandFont | '';
  radius: BrandRadius | '';
  loginTitle: string;
  loginText: string;
  emailSenderName: string;
}

/**
 * Het formulier vullen met wat er gekozen is. Een leeg veld zet het rad op de geërfde kleur: zo is
 * te zien wat er nu geldt, zonder dat het lijkt alsof er al iets gekozen is.
 */
export function draftFrom(own: BrandStyleSettings, inherited: BrandStyleSettings): BrandDraft {
  const primary = inherited.primary ?? DEFAULT_COLOURS.primary;
  const accent = inherited.accent ?? DEFAULT_COLOURS.accent;
  return {
    primary: colourState(own.primary?.toUpperCase() ?? '', own.primary ?? primary),
    accent: colourState(own.accent?.toUpperCase() ?? '', own.accent ?? accent),
    sidebar: colourState(
      own.sidebar?.toUpperCase() ?? '',
      own.sidebar ?? inherited.sidebar ?? DEFAULT_COLOURS.dark,
    ),
    fontHeading: own.fontHeading ?? '',
    fontBody: own.fontBody ?? '',
    radius: own.radius ?? '',
    loginTitle: own.loginTitle ?? '',
    loginText: own.loginText ?? '',
    emailSenderName: own.emailSenderName ?? '',
  };
}

/** Het verzoek: elk veld mee, leeg wordt `null` (erven). */
export function draftRequest(draft: BrandDraft) {
  const text = (value: string) => value.trim() || null;
  return {
    primary: text(draft.primary.text),
    accent: text(draft.accent.text),
    sidebar: text(draft.sidebar.text),
    fontHeading: draft.fontHeading || null,
    fontBody: draft.fontBody || null,
    radius: draft.radius || null,
    loginTitle: text(draft.loginTitle),
    loginText: text(draft.loginText),
    emailSenderName: text(draft.emailSenderName),
  };
}

/** Alles op `null`: "Alles terug naar standaard". */
export const RESET_ALL = {
  primary: null,
  accent: null,
  sidebar: null,
  fontHeading: null,
  fontBody: null,
  radius: null,
  loginTitle: null,
  loginText: null,
  emailSenderName: null,
} as const;

/** Het merk zoals het voorbeeld het tekent, met elk veld ingevuld. */
export interface PreviewBrand {
  /** De kleur zelf, voor vlakken. */
  primary: string;
  /** De leesbare tint, voor knoppen. */
  primaryReadable: string;
  onPrimary: string;
  accent: string;
  onAccent: string;
  /** De zijbalk zoals hij wordt: gekozen en verdonkerd, of afgeleid. Een CSS-kleur. */
  sidebar: string;
  fontHeading: BrandFont;
  fontBody: BrandFont;
  radius: BrandRadius;
  loginTitle: string | null;
  loginText: string | null;
}

/**
 * Wat het voorbeeld toont, live uit het formulier.
 *
 * Een kopie van wat de server doet (`resolveBrand`): de keuze, anders het geërfde, anders de
 * standaard. Na opslaan geldt wat de server terugstuurt; dit is alleen het voorbeeld.
 */
export function previewOf(draft: BrandDraft, inherited: BrandStyleSettings): PreviewBrand {
  const primaryHex = effectiveHex(draft.primary);
  const accentHex = effectiveHex(draft.accent);
  const p = checkColour(primaryHex);
  const a = checkColour(accentHex);
  const primaryReadable = p?.readable ?? primaryHex;

  const chosenSidebar = draft.sidebar.text.trim()
    ? checkColour(effectiveHex(draft.sidebar))?.readable
    : (inherited.sidebar ?? null);
  const isDefault =
    primaryHex.toUpperCase() === DEFAULT_COLOURS.primary.toUpperCase() &&
    accentHex.toUpperCase() === DEFAULT_COLOURS.accent.toUpperCase();
  const derived = isDefault
    ? DEFAULT_COLOURS.dark
    : `color-mix(in srgb, ${primaryReadable} 28%, ${DARK_MIX_BASE})`;

  return {
    primary: primaryHex,
    primaryReadable,
    onPrimary: p?.on ?? '#fff',
    accent: accentHex,
    onAccent: a?.on ?? '#fff',
    sidebar: chosenSidebar ?? derived,
    fontHeading: draft.fontHeading || inherited.fontHeading || BRAND_DEFAULTS.fontHeading,
    fontBody: draft.fontBody || inherited.fontBody || BRAND_DEFAULTS.fontBody,
    radius: draft.radius || inherited.radius || BRAND_DEFAULTS.radius,
    loginTitle: draft.loginTitle.trim() || inherited.loginTitle || null,
    loginText: draft.loginText.trim() || inherited.loginText || null,
  };
}

/** Wat een platform zonder keuzes erft: de standaard van UP2D8. */
export function up2d8Defaults(productName: string): BrandStyleSettings {
  return {
    primary: DEFAULT_COLOURS.primary,
    accent: DEFAULT_COLOURS.accent,
    sidebar: null,
    fontHeading: BRAND_DEFAULTS.fontHeading,
    fontBody: BRAND_DEFAULTS.fontBody,
    radius: BRAND_DEFAULTS.radius,
    loginTitle: null,
    loginText: null,
    emailSenderName: productName,
  };
}

/** Een CSS-familie met de terugval die de schil ook gebruikt (serif voor Merriweather). */
export function fontStack(font: string): string {
  return brandFontStack(font as BrandFont);
}

/** De tokens van `rond`, in de volgorde van `RADII` in `shared/theme.ts`. */
const ROND = { sm: 10, md: 14, lg: 18, xl: 24 } as const;

/**
 * Hoe rond het voorbeeld is, per stand: dezelfde set als de schil zet (`RADII`). `base` is een maat
 * uit de `rond`-set; het voorbeeld neemt de token die er het dichtst bij ligt.
 */
export function previewRadius(radius: BrandRadius, base: number): number {
  if (radius === 'rond') return base;
  const token = (Object.keys(ROND) as (keyof typeof ROND)[]).reduce((best, key) =>
    Math.abs(ROND[key] - base) < Math.abs(ROND[best] - base) ? key : best,
  );
  return parseInt(RADII[radius][token] ?? `${base}`, 10);
}

/** De hint onder een veld: eigen keuze, en wat er geldt als het leeg is. */
export function InheritHint({
  level,
  own,
  value,
  children,
}: {
  level: BrandLevel;
  /** Staat er op dit niveau iets gekozen? */
  own: boolean;
  /** Wat er geldt als het veld leeg is, leesbaar. */
  value: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      {own ? (
        <>
          <Badge tone="ok">{t('beheer.merk.ownBadge')}</Badge>{' '}
        </>
      ) : null}
      {children ? <>{children} </> : null}
      {t(level === 'tenant' ? 'beheer.merk.inheritsTenant' : 'beheer.merk.inheritsPlatform', {
        value,
      })}
    </>
  );
}

/** "3,4:1": met de decimale komma of punt van de lezer, naar beneden afgerond. */
export function ratio(value: number): string {
  return formatNumber(Math.floor(value * 10) / 10, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

interface CardProps {
  level: BrandLevel;
  draft: BrandDraft;
  onChange: (next: BrandDraft) => void;
  own: BrandStyleSettings;
  inherited: BrandStyleSettings;
}

/**
 * Kleuren: primair, accent en de optionele zijbalk. De contrastwaarschuwing onder de primaire kleur
 * is die van I1; onder de zijbalk staat welke tint de server ervan maakt.
 */
export function ColoursCard({
  level,
  draft,
  onChange,
  own,
  inherited,
  error,
  reset,
}: CardProps & {
  error: string | null;
  /** "Terug naar …" voor de kleuren, alleen als er eigen kleuren zijn. */
  reset?: ReactNode;
}) {
  const { t } = useTranslation();
  const primaryCheck = checkColour(effectiveHex(draft.primary));
  const sidebarCheck = draft.sidebar.text.trim() ? checkColour(effectiveHex(draft.sidebar)) : null;
  const hasOwn = own.primary !== null || own.accent !== null;
  const upper = (value: string | null) => value?.toUpperCase() ?? '';

  return (
    <Card testId="colours-card">
      <CardHead
        title={t('beheer.huisstijl.colours')}
        actions={
          <>
            <Badge tone={hasOwn ? 'ok' : 'neutral'}>
              {hasOwn ? t('beheer.merk.ownBadge') : t('beheer.merk.inheritedBadge')}
            </Badge>
            {hasOwn ? reset : null}
          </>
        }
      />
      <CardPad>
        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
        <ColourPicker
          title={t('branding.primary')}
          hint={t('beheer.huisstijl.primaryHint')}
          state={draft.primary}
          onChange={(primary) => onChange({ ...draft, primary })}
          placeholder={upper(inherited.primary)}
        >
          <div className="a-hint">
            <InheritHint
              level={level}
              own={own.primary !== null}
              value={upper(inherited.primary)}
            />
          </div>
          {primaryCheck && primaryCheck.whiteRatio < 4.5 ? (
            <div style={{ marginTop: 8 }}>
              <Note
                tone="attention"
                testId="contrast-warning"
                lead={t('beheer.huisstijl.contrastLead', {
                  ratio: ratio(primaryCheck.whiteRatio),
                })}
              >
                {t('beheer.huisstijl.contrastText', {
                  hex: primaryCheck.readable,
                  ratio: ratio(primaryCheck.readableRatio),
                })}
              </Note>
            </div>
          ) : null}
        </ColourPicker>
        <div className="a-sep" />
        <ColourPicker
          title={t('branding.accent')}
          hint={t('beheer.huisstijl.accentHint')}
          state={draft.accent}
          onChange={(accent) => onChange({ ...draft, accent })}
          placeholder={upper(inherited.accent)}
        >
          <div className="a-hint">
            <InheritHint level={level} own={own.accent !== null} value={upper(inherited.accent)} />
          </div>
        </ColourPicker>
        <div className="a-sep" />
        <ColourPicker
          title={t('beheer.merk.sidebar')}
          hint={t('beheer.merk.sidebarHint')}
          state={draft.sidebar}
          onChange={(sidebar) => onChange({ ...draft, sidebar })}
          placeholder={inherited.sidebar ? upper(inherited.sidebar) : t('beheer.merk.sidebarAuto')}
        >
          <div className="a-hint" data-testid="sidebar-inherit">
            <InheritHint
              level={level}
              own={own.sidebar !== null}
              value={inherited.sidebar ? upper(inherited.sidebar) : t('beheer.merk.automatic')}
            />
          </div>
          {draft.sidebar.text.trim() ? (
            <div className="a-actions" style={{ marginTop: 8 }}>
              <Btn
                variant="ghost"
                size="sm"
                testId="sidebar-auto"
                onClick={() => onChange({ ...draft, sidebar: { ...draft.sidebar, text: '' } })}
              >
                {t('beheer.merk.sidebarAuto')}
              </Btn>
            </div>
          ) : null}
          {sidebarCheck && sidebarCheck.whiteRatio < 4.5 ? (
            <div style={{ marginTop: 8 }}>
              <Note tone="attention" testId="sidebar-contrast">
                {t('beheer.merk.sidebarDarkened', { hex: sidebarCheck.readable })}
              </Note>
            </div>
          ) : null}
        </ColourPicker>
        <Note>{t('branding.readability')}</Note>
      </CardPad>
    </Card>
  );
}

/**
 * Een lettertype uit de vaste lijst. Elke naam staat in zijn eigen lettertype, voor zover de
 * browser dat in een keuzelijst toont (Chrome en Firefox op de desktop wel, een telefoon niet) en
 * het lettertype al geladen is; anders valt het netjes terug.
 */
function FontSelect({
  label,
  value,
  inherited,
  own,
  level,
  testId,
  onChange,
}: {
  label: string;
  value: BrandFont | '';
  inherited: BrandFont;
  own: boolean;
  level: BrandLevel;
  testId: string;
  onChange: (value: BrandFont | '') => void;
}) {
  const { t } = useTranslation();
  return (
    <Field
      label={label}
      hint={
        <InheritHint level={level} own={own} value={inherited}>
          {t('beheer.merk.fontHint')}
        </InheritHint>
      }
    >
      {(props) => (
        <Select
          {...props}
          value={value}
          data-testid={testId}
          style={{ fontFamily: fontStack(value || inherited) }}
          onFocus={() => {
            // Pas bij openen alle lettertypen laden, zodat elke naam in zijn eigen letter staat.
            for (const font of BRAND_FONTS) void ensureBrandFont(font);
          }}
          onChange={(event) => onChange(event.target.value as BrandFont | '')}
        >
          <option value="" style={{ fontFamily: fontStack(inherited) }}>
            {t('beheer.merk.fontInherit', { font: inherited })}
          </option>
          {BRAND_FONTS.map((font) => (
            <option key={font} value={font} style={{ fontFamily: fontStack(font) }}>
              {font}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/** Lettertype en vorm: kop- en tekstlettertype, en de afronding. */
export function TypeCard({ level, draft, onChange, own, inherited }: CardProps) {
  const { t } = useTranslation();
  const inheritedHeading = inherited.fontHeading ?? BRAND_DEFAULTS.fontHeading;
  const inheritedBody = inherited.fontBody ?? BRAND_DEFAULTS.fontBody;
  const inheritedRadius = inherited.radius ?? BRAND_DEFAULTS.radius;
  const radiusLabel = (value: BrandRadius) => t(`beheer.merk.radiusValues.${value}`);

  return (
    <Card testId="type-card">
      <CardHead title={t('beheer.merk.type')} />
      <CardPad>
        <div className="a-row">
          <FontSelect
            label={t('beheer.merk.fontHeading')}
            value={draft.fontHeading}
            inherited={inheritedHeading}
            own={own.fontHeading !== null}
            level={level}
            testId="font-heading"
            onChange={(fontHeading) => onChange({ ...draft, fontHeading })}
          />
          <FontSelect
            label={t('beheer.merk.fontBody')}
            value={draft.fontBody}
            inherited={inheritedBody}
            own={own.fontBody !== null}
            level={level}
            testId="font-body"
            onChange={(fontBody) => onChange({ ...draft, fontBody })}
          />
        </div>
        <div className="a-field">
          <div className="a-label" id={`radius-${level}`}>
            {t('beheer.merk.radius')}
          </div>
          <Segmented<BrandRadius | ''>
            labelledBy={`radius-${level}`}
            value={draft.radius}
            onChange={(radius) => onChange({ ...draft, radius })}
            options={[
              {
                value: '',
                label: t('beheer.merk.radiusInherit', { value: radiusLabel(inheritedRadius) }),
                testId: 'radius-inherit',
              },
              { value: 'rond', label: radiusLabel('rond'), testId: 'radius-rond' },
              { value: 'gemiddeld', label: radiusLabel('gemiddeld'), testId: 'radius-gemiddeld' },
              { value: 'strak', label: radiusLabel('strak'), testId: 'radius-strak' },
            ]}
          />
          <div className="a-hint">
            <InheritHint
              level={level}
              own={own.radius !== null}
              value={radiusLabel(inheritedRadius)}
            />
          </div>
        </div>
      </CardPad>
    </Card>
  );
}

/** Een teller "12 / 60" achter de hint. */
function counter(t: TFunction, value: string, max: number): string {
  return t('beheer.merk.counter', { count: value.length, max });
}

/** Aanmeldscherm: titel, tekst en de achtergrondafbeelding (die geeft het scherm mee). */
export function LoginCard({
  level,
  draft,
  onChange,
  own,
  inherited,
  background,
}: CardProps & { background: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Card testId="login-card">
      <CardHead title={t('beheer.merk.login')} />
      <CardPad>
        <Field
          label={t('beheer.merk.loginTitle')}
          hint={
            <InheritHint
              level={level}
              own={own.loginTitle !== null}
              value={inherited.loginTitle ?? t('beheer.merk.standardText')}
            >
              {counter(t, draft.loginTitle, BRAND_LIMITS.loginTitle)} ·
            </InheritHint>
          }
        >
          {(props) => (
            <Input
              {...props}
              value={draft.loginTitle}
              maxLength={BRAND_LIMITS.loginTitle}
              placeholder={inherited.loginTitle ?? t('beheer.merk.previewLoginTitle')}
              data-testid="login-title"
              onChange={(event) => onChange({ ...draft, loginTitle: event.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('beheer.merk.loginText')}
          hint={
            <InheritHint
              level={level}
              own={own.loginText !== null}
              value={inherited.loginText ?? t('beheer.merk.standardText')}
            >
              {counter(t, draft.loginText, BRAND_LIMITS.loginText)} ·{' '}
              {t('beheer.merk.loginTextHint')}
            </InheritHint>
          }
        >
          {(props) => (
            <Textarea
              {...props}
              value={draft.loginText}
              maxLength={BRAND_LIMITS.loginText}
              placeholder={inherited.loginText ?? t('beheer.merk.previewLoginText')}
              data-testid="login-text"
              onChange={(event) => onChange({ ...draft, loginText: event.target.value })}
            />
          )}
        </Field>
        {background}
      </CardPad>
    </Card>
  );
}

/** E-mail: de afzendernaam, en op het platform de voettekst (`children`). */
export function EmailCard({
  level,
  draft,
  onChange,
  own,
  inherited,
  children,
}: CardProps & { children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Card testId="email-card">
      <CardHead title={t('beheer.merk.email')} />
      <CardPad>
        <Field
          label={t('beheer.merk.senderName')}
          hint={
            <InheritHint
              level={level}
              own={own.emailSenderName !== null}
              value={inherited.emailSenderName ?? ''}
            >
              {counter(t, draft.emailSenderName, BRAND_LIMITS.emailSenderName)} ·{' '}
              {t('beheer.merk.senderHint')}
            </InheritHint>
          }
        >
          {(props) => (
            <Input
              {...props}
              value={draft.emailSenderName}
              maxLength={BRAND_LIMITS.emailSenderName}
              placeholder={inherited.emailSenderName ?? ''}
              data-testid="email-sender"
              onChange={(event) => onChange({ ...draft, emailSenderName: event.target.value })}
            />
          )}
        </Field>
        {children}
      </CardPad>
    </Card>
  );
}

/**
 * Het bevestigingsvenster van "Alles terug naar standaard".
 *
 * De logo's staan standaard **niet** mee aangevinkt: een logo is werk dat iemand opnieuw moet
 * zoeken en uploaden, en wie "standaard" zegt, denkt meestal aan kleuren en lettertypen. Wie ze
 * ook kwijt wil, vinkt het aan.
 */
export function ResetAllModal({
  open,
  onClose,
  title,
  text,
  logosLabel,
  busy,
  error,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
  logosLabel: string;
  busy: boolean;
  error: string | null;
  onConfirm: (withLogos: boolean) => void;
}) {
  const { t } = useTranslation();
  const [withLogos, setWithLogos] = useState(false);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      testId="reset-all-dialog"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            variant="danger"
            disabled={busy}
            testId="reset-all-confirm"
            onClick={() => onConfirm(withLogos)}
          >
            {t('beheer.merk.resetAllConfirm')}
          </Btn>
        </>
      }
    >
      <p style={{ margin: 0 }}>{text}</p>
      <Checkbox
        checked={withLogos}
        onChange={setWithLogos}
        label={logosLabel}
        testId="reset-all-logos"
      />
      {error ? (
        <Note tone="danger" role="alert">
          {error}
        </Note>
      ) : null}
    </Modal>
  );
}
